import { useCallback, useEffect, useRef, useState } from 'react'
import type { NextRouter } from 'next/router'
import clsx from 'clsx'
import { leaveTarget } from 'lib/community/discussions-writer'
import base from 'components/community/community.module.css'
import styles from './Writer.module.css'

/** Where the reader was going when the guard stopped them: a link's URL, or Back. */
type Leave = { kind: 'link'; url: string } | { kind: 'back' }

export interface LeaveGuard {
  /** Non-null while "Discard your changes?" is asked. */
  leave: Leave | null
  keep: () => void
  discard: () => void
}

/**
 * The edit page's unsaved-changes guard, armed while `armed` (the edit has changes and has not
 * been saved). The edit view keeps no local draft (c1607be2), so leaving loses the edit:
 *
 * - closing the tab, reloading or typing a URL: the browser's own prompt (`beforeunload` — the
 *   only thing a page may do there);
 * - a link on the page (Next `Link`s, plain anchors, Cancel): a capture-phase click listener
 *   stops the click (`preventDefault` only — Next's Link bails on `defaultPrevented`) and the
 *   in-page dialog asks;
 * - Back / Forward within the app (iOS swipe-back included): `router.beforePopState` refuses
 *   Next's handling, this page's history entry is pushed back (the URL stays the edit page's),
 *   and the dialog asks; Discard then goes back for real. A Back to another site is a document
 *   unload — the browser's prompt.
 *
 * Never `window.confirm` (5c1fe27e), and never by throwing from `routeChangeStart`: the layout's
 * PageTransition turns on its full-screen overlay at routeChangeStart and clears it only on
 * routeChangeComplete, so an aborted change would leave the page dimmed and unclickable.
 * Every handler reads refs: a save that navigates before the next render is never stopped
 * (`savedRef`), nor is the navigation Discard lets through.
 *
 * Not covered: a navigation started from code — the header search's submit / Enter
 * (DealHuntSearchBox), the avatar menu's buttons (shared AuthMenu) — no link, no popstate, no
 * unload. Pages Router has no cancellable hook for it.
 */
export function useLeaveGuard(
  armed: boolean,
  router: NextRouter | null,
  /** True once the edit is saved: the guard stands down at once, before any re-render. */
  savedRef?: { current: boolean }
): LeaveGuard {
  const [leave, setLeave] = useState<Leave | null>(null)
  const armedRef = useRef(armed)
  armedRef.current = armed
  // Discard: the guard stands down for the navigation it lets through.
  const leavingRef = useRef(false)
  const live = (): boolean =>
    armedRef.current && !leavingRef.current && !(savedRef?.current ?? false)

  useEffect(() => {
    if (!armed) {
      return undefined
    }
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (!live()) {
        return
      }
      e.preventDefault()
      // Older browsers show the prompt only when returnValue is set.
      e.returnValue = ''
    }
    const onClick = (e: MouseEvent): void => {
      if (!live()) {
        return
      }
      const anchor = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor) {
        return
      }
      const url = leaveTarget(
        e,
        {
          href: anchor.getAttribute('href') ?? '',
          target: anchor.target,
          download: anchor.hasAttribute('download'),
        },
        window.location.href
      )
      if (url) {
        e.preventDefault()
        setLeave({ kind: 'link', url })
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    document.addEventListener('click', onClick, true)
    // This page's own history entry (Next's state object, its URL), put back on Back/Forward.
    const entry = { state: window.history.state as unknown, url: window.location.href }
    router?.beforePopState(() => {
      if (!live()) {
        return true
      }
      // The browser already moved; re-push this page's entry (no popstate fires, whichever way
      // the reader went) and ask. Discard then goes back for real.
      window.history.pushState(entry.state, '', entry.url)
      setLeave({ kind: 'back' })
      return false
    })
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      document.removeEventListener('click', onClick, true)
      router?.beforePopState(() => true)
    }
    // `live` reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, router])

  const keep = useCallback(() => setLeave(null), [])
  const discard = useCallback(() => {
    const to = leave
    setLeave(null)
    if (!to) {
      return
    }
    leavingRef.current = true
    if (to.kind === 'back') {
      window.history.back()
      return
    }
    const url = new URL(to.url)
    if (router && url.origin === window.location.origin) {
      void router.push(`${url.pathname}${url.search}${url.hash}`)
    } else {
      window.location.assign(to.url)
    }
  }, [leave, router])

  return { leave, keep, discard }
}

/**
 * "Discard your changes?" — a modal `<dialog>` (focus held inside, Escape keeps editing), so a
 * link pressed far down the page still gets the question in view. Keep editing is focused first:
 * the safe side.
 */
export const LeaveDialog: React.VFC<{ guard: LeaveGuard }> = ({ guard }) => {
  const ref = useRef<HTMLDialogElement>(null)
  const keepRef = useRef<HTMLButtonElement>(null)
  const open = guard.leave !== null
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) {
      return
    }
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal()
      } else {
        dialog.setAttribute('open', '')
      }
      keepRef.current?.focus()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])
  const { keep } = guard
  return (
    <dialog
      ref={ref}
      className={styles.leaveDialog}
      aria-labelledby="leave-title"
      aria-describedby="leave-text"
      onCancel={e => {
        e.preventDefault()
        keep()
      }}
      // A close the page did not ask for (a browser's close watcher on a repeated Escape) still
      // clears the question, or the next leave would find `open` unchanged and never show it.
      onClose={keep}
    >
      <h2 id="leave-title" className={styles.leaveTitle}>
        {'Discard your changes?'}
      </h2>
      <p id="leave-text" className={styles.leaveText}>
        {"You've changed this post and haven't saved it. If you leave now, your changes are lost."}
      </p>
      <div className={styles.leaveButtons}>
        <button ref={keepRef} type="button" className={styles.secondary} onClick={keep}>
          {'Keep editing'}
        </button>
        <button
          type="button"
          className={clsx(base.btn, styles.leaveDiscard)}
          onClick={guard.discard}
        >
          {'Discard changes'}
        </button>
      </div>
    </dialog>
  )
}
